import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { 
  Activity, TrendingUp, Dumbbell, BookOpen, Brain, User, LogOut, 
  Map, Globe, Users, Heart, Camera, Menu, X, Home,
  Zap, ChevronRight, CalendarDays, Trophy, Target, Flame, CreditCard,
  Wifi, WifiOff, RefreshCw, Share2, Crown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { syncAll, getPendingSyncCount } from "@/utils/offlineStorage";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import NotificationBell from "@/components/NotificationBell";
import SyncIndicator from "@/components/SyncIndicator";
import BetaFeedbackWidget from "@/components/BetaFeedbackWidget";
import UserTierBadge from "@/components/UserTierBadge";

const Layout = ({ children, user, onLogout }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [pendingSync, setPendingSync] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [tierData, setTierData] = useState(null);
  const location = useLocation();
  const { t } = useLanguage();

  // Fetch effective tier on mount + after route changes (catches just-claimed rewards)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    fetch(`${BACKEND}/api/subscription/feature-access`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setTierData(data); })
      .catch(() => {});
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      const count = await getPendingSyncCount();
      if (count > 0) {
        setIsSyncing(true);
        await syncAll();
        setPendingSync(0);
        setIsSyncing(false);
      }
    };
    
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Check pending sync count on mount
    getPendingSyncCount().then(setPendingSync);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Manual sync handler
  const handleManualSync = async () => {
    if (!isOnline || isSyncing) return;
    setIsSyncing(true);
    await syncAll();
    setPendingSync(await getPendingSyncCount());
    setIsSyncing(false);
  };

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const ADMIN_EMAILS = ['pivotpointathletics@gmail.com'];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  const navItems = [
    { to: "/dashboard", icon: Activity, labelKey: "dashboard", color: "text-primary" },
    { to: "/grind", icon: Flame, labelKey: "break_the_cycle", color: "text-orange-500" },
    { to: "/schedule", icon: CalendarDays, labelKey: "schedule", color: "text-accent" },
    { to: "/sessions", icon: Target, labelKey: "sessions", color: "text-yellow-500" },
    { to: "/badges", icon: Trophy, labelKey: "badges", color: "text-amber-500" },
    { to: "/leaderboard", icon: Trophy, labelKey: "leaderboard", color: "text-purple-500" },
    { to: "/program", icon: Map, labelKey: "program", color: "text-blue-500" },
    { to: "/engine", icon: Dumbbell, labelKey: "engine_monitor", color: "text-orange-500" },
    { to: "/vertical", icon: TrendingUp, labelKey: "vertical", color: "text-green-500" },
    { to: "/bodyweight", icon: Heart, labelKey: "bodyweight", color: "text-red-500" },
    { to: "/weightroom", icon: Dumbbell, labelKey: "weight_room", color: "text-amber-500" },
    { to: "/progress", icon: Camera, labelKey: "progress", color: "text-purple-500" },
    { to: "/library", icon: BookOpen, labelKey: "library", color: "text-cyan-500" },
    { to: "/coach", icon: Brain, labelKey: "coach", color: "text-yellow-500" },
    { to: "/ecosystem", icon: Globe, labelKey: "ecosystem", color: "text-indigo-500" },
    { to: "/community", icon: Users, labelKey: "community", color: "text-teal-500" },
    { to: "/teams", icon: Users, labelKey: "teams", color: "text-orange-400" },
    { to: "/recovery", icon: Heart, labelKey: "recovery", color: "text-pink-400" },
    { to: "/subscription", icon: CreditCard, labelKey: "subscription", color: "text-emerald-500" },
    { to: "/share", icon: Share2, labelKey: "share", color: "text-pink-500" },
    { to: "/analytics", icon: Activity, labelKey: "analytics", color: "text-cyan-500" },
    { to: "/profile", icon: User, labelKey: "profile", color: "text-gray-400" },
    ...(isAdmin ? [{ to: "/admin", icon: Crown, labelKey: "admin", color: "text-[#FF4500]" }] : []),
  ];

  // Bottom nav items (most used)
  const bottomNavItems = [
    { to: "/dashboard", icon: Home, labelKey: "dashboard" },
    { to: "/engine", icon: Zap, labelKey: "engine_monitor" },
    { to: "/vertical", icon: TrendingUp, labelKey: "vertical" },
    { to: "/progress", icon: Camera, labelKey: "progress" },
    { to: "/profile", icon: User, labelKey: "profile" },
  ];

  // BETA MODE flag — toggle in grind/GrindConstants.js for single source of truth
  const BETA_MODE = false;

  return (
    <div className="flex min-h-screen bg-background">
      {/* Beta Feedback Widget — global, floating */}
      {BETA_MODE && <BetaFeedbackWidget />}
      {/* BETA Banner */}
      {BETA_MODE && (
        <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-cyan-600 via-cyan-500 to-cyan-600 text-white text-center py-1.5 text-xs font-bold uppercase tracking-widest z-[60]">
          {t('beta_banner')}
        </div>
      )}

      {/* Offline Banner */}
      {!isOnline && (
        <div className={`fixed ${BETA_MODE ? 'top-7' : 'top-0'} left-0 right-0 bg-amber-500 text-black text-center py-2 text-sm font-bold z-[60] flex items-center justify-center gap-2`}>
          <WifiOff className="w-4 h-4" />
          <span>{t('offline')}</span>
          {pendingSync > 0 && (
            <span className="bg-black/20 px-2 py-0.5 rounded text-xs">{pendingSync} pending</span>
          )}
        </div>
      )}

      {/* Sync indicator when online with pending items */}
      {isOnline && pendingSync > 0 && (
        <div className={`fixed ${BETA_MODE ? 'top-7' : 'top-0'} left-0 right-0 bg-emerald-500 text-white text-center py-2 text-sm font-medium z-[60] flex items-center justify-center gap-2`}>
          {isSyncing ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span>{pendingSync} {t('sync_ready')}...</span>
            </>
          ) : (
            <>
              <Wifi className="w-4 h-4" />
              <span>{pendingSync} {t('sync_ready')}</span>
              <button 
                onClick={handleManualSync}
                className="bg-white/20 hover:bg-white/30 px-2 py-0.5 rounded text-xs font-bold"
              >
                {t('sync_now')}
              </button>
            </>
          )}
        </div>
      )}

      {/* Mobile Header */}
      <header className={`md:hidden fixed ${BETA_MODE ? 'top-7' : 'top-0'} left-0 right-0 bg-black/95 backdrop-blur-lg border-b border-border z-50 safe-top`}>
        <div className="flex items-center justify-between px-4 h-14">
          <button
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-btn"
            className="p-2 -ml-2 text-white active:bg-white/10 rounded-sm touch-manipulation"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-2">
            <img src="/icons/icon-72x72.png" alt="Pivot" className="w-8 h-8 rounded-sm" />
            <h1 className="text-lg font-heading font-black text-primary tracking-tighter uppercase">
              PIVOT
            </h1>
          </div>
          
          <div className="flex items-center gap-1">
            <NotificationBell />
            <LanguageSelector variant="minimal" />
          </div>
        </div>
      </header>

      {/* Mobile Slide-out Menu */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="md:hidden fixed inset-0 bg-black/80 z-[70] touch-manipulation"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="md:hidden fixed top-0 left-0 bottom-0 w-[85%] max-w-[320px] bg-black z-[80] overflow-y-auto safe-left animate-slide-in">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h1 className="text-xl font-heading font-black text-primary tracking-tighter uppercase">
                  {t('app_name')}
                </h1>
                <p className="text-xs text-muted-foreground">{t('tagline').substring(0, 20)}...</p>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-menu-close-btn"
                className="p-2 text-white active:bg-white/10 rounded-sm touch-manipulation"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{user?.name || 'Athlete'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
                </div>
              </div>
              {/* Effective tier badge — shows feedback reward / Stripe / lifetime status */}
              {tierData && (
                <div className="mt-3">
                  <UserTierBadge userTierData={tierData} />
                </div>
              )}
            </div>

            {/* Install App Banner */}
            {installPrompt && (
              <div className="p-4 border-b border-border">
                <button
                  onClick={handleInstall}
                  className="w-full flex items-center justify-between p-3 bg-primary/10 border border-primary/30 rounded-sm active:bg-primary/20 touch-manipulation"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-white">Install App</p>
                      <p className="text-xs text-muted-foreground">Add to home screen</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-primary" />
                </button>
              </div>
            )}

            {/* Navigation */}
            <nav className="p-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.labelKey}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-4 py-3 transition-all touch-manipulation ${
                      isActive
                        ? "bg-white/[0.03] text-white border-l-2 border-[#FF4500]"
                        : "text-white/40 hover:text-white/70 hover:bg-white/[0.02] border-l-2 border-transparent"
                    }`
                  }
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-4 h-4" />
                    <span className="font-medium text-sm">{t(item.labelKey)}</span>
                  </div>
                </NavLink>
              ))}
            </nav>

            {/* Logout */}
            <div className="p-4 border-t border-border mt-auto">
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-4 px-4 py-4 text-red-500 rounded-sm active:bg-red-500/10 touch-manipulation"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">{t('logout')}</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex md:flex-col w-56 bg-[#050505] border-r border-white/[0.06] sticky ${BETA_MODE ? 'top-7' : 'top-0'} h-screen`}>
        <div className="p-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <img src="/icons/icon-72x72.png" alt="Pivot" className="w-10 h-10 rounded-sm flex-shrink-0" />
            <h1 className="text-xl font-heading text-[#FF4500] tracking-tight uppercase leading-tight">
              PIVOT<br/>PERFORMANCE
            </h1>
            {BETA_MODE && (
              <span className="bg-[#CCFF00] text-black text-[8px] font-mono font-bold px-1.5 py-0.5">BETA</span>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-[9px] font-mono text-white/30 uppercase tracking-[0.2em]">{t('tagline').substring(0, 18)}...</p>
            <div className="flex items-center gap-1">
              <NotificationBell />
              <LanguageSelector variant="minimal" />
            </div>
          </div>
        </div>
        
        <nav className="flex-1 px-0 py-3 space-y-0 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.labelKey}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 transition-all border-l-2 ${
                  isActive
                    ? "bg-white/[0.03] text-white border-[#FF4500]"
                    : "text-white/40 hover:text-white/70 hover:bg-white/[0.02] border-transparent"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span className="text-sm">{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/[0.06]">
          <div className="mb-2">
            <SyncIndicator />
          </div>
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-bold text-white truncate">{user?.name || 'Athlete'}</p>
            <p className="text-[9px] font-mono text-white/30 truncate">{user?.email || ''}</p>
          </div>
          {tierData && (
            <div className="mb-2 px-1">
              <UserTierBadge userTierData={tierData} />
            </div>
          )}
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start gap-3 text-white/30 hover:text-white hover:bg-white/[0.03] text-sm rounded-none"
            onClick={onLogout}
          >
            <LogOut className="w-4 h-4" />
            <span>{t('logout')}</span>
          </Button>
          <NavLink
            to="/privacy"
            className="flex items-center justify-center gap-1 mt-2 text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors"
          >
            <span>Privacy Policy</span>
          </NavLink>
          <NavLink
            to="/terms"
            className="flex items-center justify-center gap-1 mt-1 text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors"
          >
            <span>Terms of Service</span>
          </NavLink>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 min-w-0 overflow-x-hidden ${!isOnline ? 'pt-10' : ''} ${BETA_MODE ? 'pt-24 md:pt-10' : 'pt-14 md:pt-0'} pb-20 md:pb-0`}>
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-lg border-t border-border z-50 safe-bottom">
        <div className="flex justify-around items-center h-16 px-1">
          {bottomNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.labelKey}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 flex-1 py-2 rounded-sm transition-all touch-manipulation active:scale-95 ${
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground active:text-white"
                }`
              }
            >
              <item.icon className={`w-6 h-6`} />
              <span className="text-[10px] font-medium">{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
